import React, { useCallback, useEffect, useMemo, useRef } from "react";
import styled from "styled-components";
import { useDrag } from "react-dnd";

import { Grid, IconButton, Typography, Box } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

import Button from "@ingka/button";

import { useDispatch, useSchema, useSearchElement, useSearchElementParents, useSelection } from "../../core/context";
import { DndItems } from "../../core/dnd";
import { SchemaIcon, UISchemaIcon } from "../../core/icons";
import { Actions } from "../../core/model";
import { EditorUISchemaElement, getUISchemaPath, hasChildren } from "../../core/model/uischema";
import { isEditorControl, tryFindByUUID } from "../../core/util/schemasUtil";
import WarningModal from "../../../WarningModal/WarningModal";

const ButtonWrapper = styled.div`
	display: flex;
	gap: 1rem;
	justify-content: center;
`;

const StyledEditorElement = styled(Grid)<{
	$isDragging: boolean;
	$isSelected: boolean;
	$isBoolean: boolean;
}>`
	border: 1px solid #d3d3d3;
	padding: 8px;
	opacity: ${({ $isDragging }) => ($isDragging ? 0.5 : 1)};
	width: 100%;
	align-self: baseline;
	overflow: hidden;
	background-color: ${({ $isSelected }) => ($isSelected ? "rgba(63, 81, 181, 0.08)" : "transparent")};
	border-color: ${({ $isSelected }) => ($isSelected ? "#a9a9a9" : "#d3d3d3")};

	${({ $isBoolean }) =>
		$isBoolean &&
		`
        & .MuiFormControlLabel-root {
            margin-left: 0;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        & .MuiTypography-root {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
    `}
`;

const ElementHeader = styled(Grid)`
	margin-bottom: 2px;

	&:hover .element-controls {
		opacity: 1;
	}
`;

const ElementHeaderLeft = styled(Box)`
	display: flex;
	align-items: center;
	justify-content: center;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

const ElementControls = styled(Grid)`
	opacity: 0;
`;

const IconWrapper = styled(Box)`
	margin-right: 8px;
	margin-left: 0;
	width: fit-content;
`;

const RuleWrapper = styled(Box)`
	display: flex;
	margin-right: 8px;
`;

const PathWrapper = styled(Box)`
	display: flex;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

const RuleText = styled(Typography)`
	font-weight: bolder;
	margin-right: 4px;
`;

const RuleEffectText = styled(Typography)`
	font-style: italic;
`;

const EllipsisText = styled(Typography)`
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

export interface EditorElementProps {
	wrappedElement: EditorUISchemaElement;
	elementIcon?: React.ReactNode;
	children?: React.ReactNode;
}

export const EditorElement: React.FC<EditorElementProps> = ({ wrappedElement, elementIcon, children }) => {
	const ref = useRef<any>(null);
	const schema = useSchema();
	const [selection, setSelection] = useSelection();
	const [searchElement] = useSearchElement();
	const [searchElementParent, setSearchElementParent] = useSearchElementParents();

	const dispatch = useDispatch();
	const [openConfirmRemoveDialog, setOpenConfirmRemoveDialog] = React.useState(false);
	const elementSchema = tryFindByUUID(schema, wrappedElement.linkedSchemaElement);

	const [{ isDragging }, drag] = useDrag({
		type: DndItems.moveUISchemaElement(wrappedElement, elementSchema).type,
		item: DndItems.moveUISchemaElement(wrappedElement, elementSchema),
		collect: (monitor) => ({
			isDragging: !!monitor.isDragging(),
		}),
	});

	const uiPath = getUISchemaPath(wrappedElement);
	const isSelected = selection?.uuid === wrappedElement.uuid;
	const isSearching = useMemo(
		() => wrappedElement?.linkedSchemaElement && wrappedElement?.linkedSchemaElement === searchElement?.uuid,
		[searchElement?.uuid, wrappedElement?.linkedSchemaElement]
	);

	const traverseParents = useCallback((obj: any, uuids: any[]) => {
		if (obj && typeof obj === "object" && Object.prototype.hasOwnProperty.call(obj, "parent")) {
			uuids.push(obj.uuid);
			traverseParents(obj.parent, uuids);
		}
	}, []);

	useEffect(() => {
		if (isSearching) {
			const uuids: any[] = [];
			traverseParents(wrappedElement, uuids);
			if (uuids.length) {
				setSearchElementParent(uuids);
			}
		}
	}, [isSearching, setSearchElementParent, traverseParents, wrappedElement]);

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			if (isSearching) {
				ref.current?.scrollIntoView?.({ behavior: "smooth" });
				setSearchElementParent([]);
			}
		}, 1000);

		return () => clearTimeout(timeoutId);
	}, [isSearching, setSearchElementParent]);

	const isParentOfSearchElement = searchElementParent.includes(wrappedElement.uuid);
	const ruleEffect = wrappedElement.rule?.effect.toUpperCase();

	const icon =
		elementIcon ??
		(elementSchema ? <SchemaIcon type={elementSchema.type} /> : <UISchemaIcon type={wrappedElement.type} />);

	const shouldHighlight =
		isSelected ||
		isSearching ||
		(isParentOfSearchElement && wrappedElement.type === "VerticalLayout") ||
		(isParentOfSearchElement && wrappedElement.type === "HorizontalLayout");

	return (
		<>
			<StyledEditorElement
				item
				data-cy={`editorElement-${uiPath}`}
				ref={drag}
				$isDragging={isDragging}
				$isSelected={shouldHighlight}
				$isBoolean={elementSchema?.type === "boolean"}
				onClick={(event) => {
					event.stopPropagation();
					setSelection({ uuid: wrappedElement.uuid });
				}}
			>
				<ElementHeader
					item
					container
					direction="row"
					wrap="nowrap"
					ref={ref}
					data-cy={`editorElement-${uiPath}-header`}
				>
					<ElementHeaderLeft>
						<IconWrapper>{icon}</IconWrapper>

						{ruleEffect ? (
							<RuleWrapper>
								<RuleText variant="subtitle2">R</RuleText>
								<RuleEffectText variant="caption">{`(${ruleEffect})`}</RuleEffectText>
							</RuleWrapper>
						) : null}

						{isEditorControl(wrappedElement) && (
							<PathWrapper>
								<EllipsisText variant="caption">{wrappedElement.scope}</EllipsisText>
							</PathWrapper>
						)}
					</ElementHeaderLeft>

					<ElementControls item container className="element-controls" justifyContent="flex-end" alignItems="center" xs>
						<IconButton
							data-cy={`editorElement-${uiPath}-removeButton`}
							size="small"
							title="Remove Property"
							onClick={() => {
								hasChildren(wrappedElement)
									? setOpenConfirmRemoveDialog(true)
									: dispatch(Actions.removeUiSchemaElement(wrappedElement.uuid));
							}}
						>
							<DeleteIcon />
						</IconButton>
					</ElementControls>
				</ElementHeader>

				{
					//@ts-ignore
					wrappedElement.options?.addressOrder != true && wrappedElement?.addressOrder != true && children
				}
			</StyledEditorElement>

			<WarningModal
				modalId={"remove-elements-dialog"}
				modalTitle={"Warning"}
				showWarningModal={openConfirmRemoveDialog}
				setShowWarningModal={setOpenConfirmRemoveDialog}
				children={
					<>
						<p>Remove element and all its contents from the UI Schema?</p>
						<ButtonWrapper>
							<Button
								id="remove-cancel-button"
								text="Cancel"
								type="emphasised"
								size="small"
								onClick={() => setOpenConfirmRemoveDialog(false)}
							/>
							<Button
								id="remove-schema-button"
								text="Remove"
								type="secondary"
								size="small"
								onClick={() => {
									dispatch(Actions.removeUiSchemaElement(wrappedElement.uuid));
									setOpenConfirmRemoveDialog(false);
								}}
							/>
						</ButtonWrapper>
					</>
				}
				variant="cautionary"
				dismissable={false}
			/>
		</>
	);
};
